import React, { memo, useCallback, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import useDropdownPosition from '../../../hooks/useDropdownPosition';

/**
 * SelectCell - Portal-based dropdown for generic select columns in the matrix.
 * Replaces native <select> to avoid clipping in virtualized/overflow containers.
 * Supports allowAddNew to let users type and add new options on the fly.
 */
const SelectCell = memo(({
  value,
  rowId,
  columnId,
  options = [],
  hasEditPermission = false,
  onUpdate,
  onSave,
  isDragging = false,
  onDragFillStart,
  rowIndex,
  showDragHandle = false,
  isRetrying = false,
  allowAddNew = false,
  onOptionAdded,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newValue, setNewValue] = useState('');
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const getPosition = useDropdownPosition({ menuHeight: 300, menuWidth: 160 });

  const handleSelect = useCallback((newVal) => {
    setIsOpen(false);
    setIsAddingNew(false);
    setNewValue('');
    if (newVal === value) return;

    if (onUpdate) {
      onUpdate(rowId, columnId, newVal);
    }
    if (onSave) {
      onSave(rowId, columnId, value, newVal);
    }
  }, [value, rowId, columnId, onUpdate, onSave]);

  const handleAddNew = useCallback(() => {
    const trimmed = newValue.trim();
    if (!trimmed) return;
    if (onOptionAdded) {
      onOptionAdded(columnId, trimmed);
    }
    handleSelect(trimmed);
  }, [newValue, columnId, onOptionAdded, handleSelect]);

  const handleClick = useCallback(() => {
    if (hasEditPermission && !isDragging) {
      setIsOpen(!isOpen);
      setIsAddingNew(false);
      setNewValue('');
    }
  }, [hasEditPermission, isDragging, isOpen]);

  const handleDragStart = useCallback((e) => {
    e.stopPropagation();
    if (onDragFillStart) {
      onDragFillStart(e, rowIndex, columnId, value);
    }
  }, [onDragFillStart, rowIndex, columnId, value]);

  if (!hasEditPermission) {
    return (
      <div style={{ fontSize: '13px', textAlign: 'center', color: '#212529' }}>
        {value || ''}
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      <div
        ref={triggerRef}
        onClick={handleClick}
        style={{
          width: showDragHandle ? 'calc(100% - 10px)' : '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '13px',
          cursor: 'pointer',
          padding: '2px 4px',
          color: value ? '#212529' : '#999',
          userSelect: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || '\u2014'}
        </span>
        <span style={{ fontSize: '9px', color: '#999', marginLeft: '4px' }}>▼</span>
      </div>

      {isOpen && createPortal(
        <>
          <div
            onClick={() => { setIsOpen(false); setIsAddingNew(false); setNewValue(''); }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 9998
            }}
          />
          {(() => {
            const pos = getPosition(triggerRef.current);
            return (
              <div
                style={{
                  position: 'fixed',
                  ...(pos.openUpward
                    ? { bottom: `${window.innerHeight - pos.top}px` }
                    : { top: `${pos.top}px` }),
                  ...(pos.right !== undefined
                    ? { right: `${pos.right}px` }
                    : { left: `${pos.left}px` }),
                  transform: pos.transform,
                  backgroundColor: 'white',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                  zIndex: 9999,
                  minWidth: '120px',
                  maxHeight: `${Math.min(300, pos.maxHeight)}px`,
                  overflowY: 'auto'
                }}
              >
                {options.map(opt => (
                  <div
                    key={opt}
                    onClick={() => handleSelect(opt)}
                    style={{
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      textAlign: 'center',
                      backgroundColor: opt === value ? '#e7f3ff' : 'white',
                      borderBottom: '1px solid #f0f0f0',
                      color: opt ? '#333' : '#999'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = opt === value ? '#e7f3ff' : 'white'}
                  >
                    {opt || '\u2014'}
                  </div>
                ))}
                {allowAddNew && (
                  <>
                    {!isAddingNew ? (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsAddingNew(true);
                          setTimeout(() => inputRef.current?.focus(), 50);
                        }}
                        style={{
                          padding: '6px 12px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          textAlign: 'center',
                          color: '#28a745',
                          fontWeight: 'bold',
                          borderTop: '2px solid #e0e0e0',
                          backgroundColor: '#f8fff8'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e8f5e9'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fff8'}
                      >
                        + Add New
                      </div>
                    ) : (
                      <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          padding: '6px 8px',
                          borderTop: '2px solid #e0e0e0',
                          backgroundColor: '#f8fff8',
                          display: 'flex',
                          gap: '4px'
                        }}
                      >
                        <input
                          ref={inputRef}
                          type="text"
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddNew();
                            if (e.key === 'Escape') { setIsAddingNew(false); setNewValue(''); }
                          }}
                          placeholder="New type..."
                          style={{
                            flex: 1,
                            padding: '4px 6px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            fontSize: '11px',
                            minWidth: 0,
                          }}
                        />
                        <button
                          onClick={handleAddNew}
                          disabled={!newValue.trim()}
                          style={{
                            padding: '4px 8px',
                            border: 'none',
                            borderRadius: '3px',
                            backgroundColor: newValue.trim() ? '#28a745' : '#ccc',
                            color: 'white',
                            cursor: newValue.trim() ? 'pointer' : 'default',
                            fontSize: '11px',
                            fontWeight: 'bold',
                          }}
                        >
                          ✓
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()}
        </>,
        document.body
      )}

      {showDragHandle && onDragFillStart && (
        <div
          onMouseDown={handleDragStart}
          style={{
            position: 'absolute',
            bottom: '1px',
            right: '1px',
            width: '8px',
            height: '8px',
            backgroundColor: '#007bff',
            cursor: 'crosshair',
            borderRadius: '1px',
            zIndex: 20,
            boxShadow: '0 0 2px rgba(0,0,0,0.4)',
            pointerEvents: 'auto',
          }}
          title="Drag to fill cells"
        />
      )}
    </div>
  );
});

SelectCell.displayName = 'SelectCell';

export default SelectCell;
